import type {
  InventoryOwnership,
  InventoryParams,
  PackBuilderSort,
} from '../../../lib/api/operatorConsole';
import type { SelectOption } from '../../../components/ui/Select';

/** Raw filter inputs of the ADD-CARDS picker. Everything is URL-shaped
 *  (strings / string arrays) so the mode can keep them in ?query and survive a
 *  reload. There is no membership filter: the picker never shows a card the
 *  pack already holds. */
export interface InventoryFilterValues {
  q: string;
  ownership: InventoryOwnership;
  /** Collectibles::Category id as a string; '' = all (CONTRACT §4.13). */
  category_id: string;
  fmv_min: string;
  fmv_max: string;
  sport: string[];
  grader: string[];
  grade: string[];
  year_from: string;
  year_to: string;
  set: string[];
  rookie: boolean;
  pokemon_rarity: string[];
  sort: PackBuilderSort;
  group_variations: boolean;
}

export const EMPTY_INVENTORY_FILTERS: InventoryFilterValues = {
  q: '',
  ownership: 'any',
  category_id: '',
  fmv_min: '',
  fmv_max: '',
  sport: [],
  grader: [],
  grade: [],
  year_from: '',
  year_to: '',
  set: [],
  rookie: false,
  pokemon_rarity: [],
  sort: 'fmv_desc',
  group_variations: false,
};

export const SORT_OPTIONS: SelectOption[] = [
  { value: 'fmv_desc', label: 'Value: high → low' },
  { value: 'fmv_asc', label: 'Value: low → high' },
  { value: 'newest', label: 'Newest first' },
  { value: 'title', label: 'Title A–Z' },
];

export const OWNERSHIP_OPTIONS: SelectOption[] = [
  { value: 'any', label: 'Shared + mine' },
  { value: 'mine', label: 'Mine only' },
  { value: 'shared', label: 'Shared only' },
];

const OWNERSHIP_VALUES: readonly InventoryOwnership[] = ['any', 'mine', 'shared'];
const SORT_VALUES: readonly PackBuilderSort[] = ['fmv_desc', 'fmv_asc', 'newest', 'title'];

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export const asOwnership = (value: string | null): InventoryOwnership =>
  oneOf(value, OWNERSHIP_VALUES, 'any');
export const asSort = (value: string | null): PackBuilderSort =>
  oneOf(value, SORT_VALUES, 'fmv_desc');

const TEXT_KEYS = ['q', 'fmv_min', 'fmv_max', 'year_from', 'year_to', 'category_id'] as const;
const LIST_KEYS = ['sport', 'grader', 'grade', 'set', 'pokemon_rarity'] as const;

/** URL → filters. Unknown or missing params fall back to the defaults. */
export function filtersFromSearchParams(sp: URLSearchParams): InventoryFilterValues {
  const filters: InventoryFilterValues = {
    ...EMPTY_INVENTORY_FILTERS,
    sport: [],
    grader: [],
    grade: [],
    set: [],
    pokemon_rarity: [],
  };
  for (const key of TEXT_KEYS) filters[key] = sp.get(key) ?? '';
  for (const key of LIST_KEYS) filters[key] = sp.getAll(key);
  filters.ownership = asOwnership(sp.get('ownership'));
  filters.sort = asSort(sp.get('sort'));
  filters.rookie = sp.get('rookie') === '1';
  filters.group_variations = sp.get('group_variations') === '1';
  return filters;
}

export function pageFromSearchParams(sp: URLSearchParams): number {
  const page = Number(sp.get('page'));
  return Number.isInteger(page) && page > 0 ? page : 1;
}

/** filters + page → URL. Only `tab` and the add-mode flag survive from the
 *  current params; defaults are omitted so an untouched filter set is a clean
 *  URL. */
export function writeFilters(
  current: URLSearchParams,
  filters: InventoryFilterValues,
  page: number,
): URLSearchParams {
  const next = new URLSearchParams();
  const tab = current.get('tab');
  if (tab) next.set('tab', tab);
  const add = current.get('add');
  if (add) next.set('add', add);
  for (const key of TEXT_KEYS) {
    if (filters[key].trim()) next.set(key, filters[key].trim());
  }
  for (const key of LIST_KEYS) {
    for (const value of filters[key]) next.append(key, value);
  }
  if (filters.ownership !== EMPTY_INVENTORY_FILTERS.ownership) {
    next.set('ownership', filters.ownership);
  }
  if (filters.sort !== EMPTY_INVENTORY_FILTERS.sort) next.set('sort', filters.sort);
  if (filters.rookie) next.set('rookie', '1');
  if (filters.group_variations) next.set('group_variations', '1');
  if (page > 1) next.set('page', String(page));
  return next;
}

/** True when any filter (not the sort) differs from the defaults. */
export function hasActiveFilters(filters: InventoryFilterValues): boolean {
  return writeFilters(new URLSearchParams(), { ...filters, sort: 'fmv_desc' }, 1).toString() !== '';
}

function numberOrUndefined(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/** filters → request params. Blanks and NaN are dropped rather than sent for
 *  the backend to reject. */
export function toInventoryParams(
  filters: InventoryFilterValues,
  packId: number,
  page: number,
  per: number,
): InventoryParams {
  const q = filters.q.trim();
  const fmvMin = numberOrUndefined(filters.fmv_min);
  const fmvMax = numberOrUndefined(filters.fmv_max);
  const yearFrom = numberOrUndefined(filters.year_from);
  const yearTo = numberOrUndefined(filters.year_to);
  const categoryId = numberOrUndefined(filters.category_id);
  return {
    pack_id: packId,
    page,
    per,
    ownership: filters.ownership,
    sort: filters.sort,
    ...(q ? { q } : {}),
    ...(fmvMin !== undefined ? { fmv_min: fmvMin } : {}),
    ...(fmvMax !== undefined ? { fmv_max: fmvMax } : {}),
    ...(yearFrom !== undefined ? { year_from: yearFrom } : {}),
    ...(yearTo !== undefined ? { year_to: yearTo } : {}),
    ...(categoryId !== undefined ? { category_id: categoryId } : {}),
    ...(filters.sport.length ? { sport: filters.sport } : {}),
    ...(filters.grader.length ? { grader: filters.grader } : {}),
    ...(filters.grade.length ? { grade: filters.grade } : {}),
    ...(filters.set.length ? { set: filters.set } : {}),
    ...(filters.pokemon_rarity.length ? { pokemon_rarity: filters.pokemon_rarity } : {}),
    ...(filters.rookie ? { rookie: true } : {}),
    ...(filters.group_variations ? { group_variations: true } : {}),
  };
}
